// Calendar grid mensual — vista protagónica del journal.
// Lunes-domingo · heatmap P&L relativo al peor/mejor día del mes.
// Today badge dorado · selección con anillo accent · indicadores open/pending.

import type { ReactElement } from 'react';
import { cn } from '../../lib/cn';

export interface DayAggregate {
  date: string;
  trades: number;
  wins: number;
  losses: number;
  pnl: number;
  pending?: number;
  open?: number;
}

interface Props {
  month: string;
  days: DayAggregate[];
  selectedDate: string | null;
  onSelectDate: (date: string | null) => void;
  compact?: boolean;
}

function buildGrid(monthStr: string): { iso: string; inMonth: boolean }[] {
  const [yearStr, mStr] = monthStr.split('-');
  const year = Number(yearStr);
  const m = Number(mStr) - 1;
  const firstOfMonth = new Date(Date.UTC(year, m, 1));
  const firstDow = (firstOfMonth.getUTCDay() + 6) % 7;
  const lastOfMonth = new Date(Date.UTC(year, m + 1, 0));
  const daysInMonth = lastOfMonth.getUTCDate();
  const cells: { iso: string; inMonth: boolean }[] = [];

  for (let i = firstDow - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(year, m, -i));
    cells.push({ iso: d.toISOString().slice(0, 10), inMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(Date.UTC(year, m, d));
    cells.push({ iso: date.toISOString().slice(0, 10), inMonth: true });
  }
  while (cells.length % 7 !== 0) {
    const last = cells[cells.length - 1]!;
    const lastDate = new Date(last.iso + 'T00:00:00Z');
    const next = new Date(lastDate.getTime() + 24 * 60 * 60 * 1000);
    cells.push({ iso: next.toISOString().slice(0, 10), inMonth: false });
  }
  return cells;
}

export function MonthCalendar({
  month,
  days,
  selectedDate,
  onSelectDate,
  compact = false,
}: Props): ReactElement {
  const grid = buildGrid(month);
  const byDate = new Map(days.map((d) => [d.date, d]));
  const maxAbsPnl = Math.max(...days.map((d) => Math.abs(d.pnl)), 1);
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="rounded-2xl border border-surface-border bg-surface-elevated overflow-hidden shadow-soft">
      <div className="grid grid-cols-7 border-b border-surface-border bg-surface-panel/60">
        {['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'].map((d, i) => (
          <div
            key={d}
            className={cn(
              'px-2 py-2.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-center',
              i >= 5 ? 'text-accent-500/80' : 'text-ink-subtle',
            )}
          >
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {grid.map((cell) => {
          const agg = byDate.get(cell.iso);
          const dayNum = Number(cell.iso.slice(8));
          const isSelected = selectedDate === cell.iso;
          const isToday = cell.iso === today;
          const intensity = agg && agg.pnl !== 0 ? Math.abs(agg.pnl) / maxAbsPnl : 0;
          const isWin = agg && agg.pnl > 0;
          const isLoss = agg && agg.pnl < 0;

          const bgStyle: React.CSSProperties = {};
          if (isWin) {
            bgStyle.backgroundColor = `rgba(20, 184, 142, ${0.10 + intensity * 0.42})`;
          } else if (isLoss) {
            bgStyle.backgroundColor = `rgba(226, 105, 96, ${0.10 + intensity * 0.42})`;
          }

          const selectionRing =
            isSelected && cell.inMonth
              ? 'ring-2 ring-inset ring-accent-500'
              : '';

          return (
            <button
              key={cell.iso}
              type="button"
              onClick={() => onSelectDate(isSelected ? null : cell.iso)}
              disabled={!cell.inMonth && !agg}
              style={bgStyle}
              className={cn(
                'relative text-left transition-all duration-150 group outline-none',
                'border-b border-r border-surface-border/60 last:border-r-0',
                compact ? 'aspect-square p-1.5 sm:p-2' : 'aspect-[1.15/1] sm:aspect-[1.3/1] p-2 sm:p-2.5',
                cell.inMonth ? 'text-ink' : 'text-ink-faint bg-surface-panel/30',
                !cell.inMonth && !agg && 'cursor-default',
                cell.inMonth && !isSelected && 'hover:brightness-[1.05] hover:contrast-[1.05]',
                selectionRing,
              )}
            >
              <div className="flex items-start justify-between gap-1">
                <span
                  className={cn(
                    'text-xs font-semibold tabular-nums',
                    isToday &&
                      'inline-flex items-center justify-center w-5 h-5 rounded-full bg-accent-500 text-stone-950 shadow-[0_0_0_2px_rgb(212_136_28_/_0.25)]',
                  )}
                >
                  {dayNum}
                </span>
                <div className="flex items-center gap-1">
                  {agg && (agg.open ?? 0) > 0 && (
                    <span
                      title={`${agg.open} trade(s) abierto(s)`}
                      className="relative inline-block w-1.5 h-1.5 rounded-full bg-info-DEFAULT"
                    >
                      <span className="absolute inset-0 rounded-full bg-info-DEFAULT animate-ping opacity-60" />
                    </span>
                  )}
                  {agg && (agg.pending ?? 0) > 0 && (
                    <span
                      title={`${agg.pending} registro(s) pendiente(s)`}
                      className="text-[9px] font-bold text-warn-DEFAULT leading-none"
                    >
                      ⚠
                    </span>
                  )}
                  {agg && (
                    <span className="text-[9px] text-ink-subtle font-mono tabular-nums">
                      {agg.trades}
                    </span>
                  )}
                </div>
              </div>
              {agg && agg.pnl !== 0 && (
                <div
                  className={cn(
                    'mt-1 font-mono tabular-nums font-bold tracking-tight',
                    compact ? 'text-[11px]' : 'text-xs sm:text-sm',
                    agg.pnl > 0 ? 'text-win-DEFAULT' : 'text-loss-DEFAULT',
                  )}
                >
                  {agg.pnl > 0 ? '+' : ''}
                  {agg.pnl.toLocaleString('es-CL', { maximumFractionDigits: 0 })}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
